<template>
    <div>
        <v-card flat>
            <v-card-title>
                <span class="text-h5">{{ title }}</span>
                <v-btn
                    icon
                    href="https://files.gestionuniformes.cl/unf/CargaMasivaStock.xlsx"
                    target="_blank"
                >
                    <v-icon color="green">mdi-microsoft-excel</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-row>
                        <v-col cols="12" md="8">
                            <v-file-input
                                v-model="currentFile"
                                label="Seleccionar archivo Excel (.xlsx, .xls)"
                                accept=".xlsx, .xls"
                                placeholder="Haz clic para buscar el archivo"
                                prepend-icon="mdi-microsoft-excel"
                                outlined
                                dense
                                @change="resetData"
                            ></v-file-input>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-btn
                                color="success"
                                class="white--text"
                                :disabled="!currentFile"
                                @click="readExcelFile"
                                block
                            >
                                Procesar Archivo
                            </v-btn>
                        </v-col>
                    </v-row>

                    <div v-if="excelData.length > 0">
                        <v-divider class="my-3"></v-divider>
                        <p class="subtitle-1">
                            Vista previa de datos ({{ excelData.length }}
                            filas):
                        </p>
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Buscar"
                            single-line
                            hide-details
                        ></v-text-field>
                        <v-data-table
                            :headers="headers"
                            :items="excelData"
                            :items-per-page="5"
                            :search="search"
                            class="elevation-1 custom-font"
                        >
                            <template v-slot:item.flag="{ item }">
                                <v-icon :color="item.flag ? 'green' : 'red'">
                                    {{
                                        item.flag
                                            ? "mdi-check-circle"
                                            : "mdi-alert-circle"
                                    }}
                                </v-icon>
                            </template>
                            <template v-slot:item.errors="{ item }">
                                <v-chip
                                    v-if="item.errors && item.errors.length > 0"
                                    small
                                    color="error"
                                    text-color="white"
                                    class="ma-1"
                                    v-for="(error, index) in item.errors.split(
                                        ', ',
                                    )"
                                    :key="index"
                                >
                                    {{ error }}
                                </v-chip>
                                <span v-else>-</span>
                            </template>
                        </v-data-table>
                    </div>

                    <div v-else-if="loading" class="text-center py-5">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                        <p>Leyendo archivo...</p>
                    </div>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-alert shaped dense dark type="error" v-if="hasErrors">
                    Se han detectado filas con errores. No se puede continuar
                    hasta corregirlos.
                </v-alert>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="cancelAction">
                    Cancelar
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="confirmData"
                    v-if="excelData.length > 0 && !hasErrors"
                >
                    Cargar Inventario
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import { headersMassiveInventory } from "../commons/headers";
export default {
    name: "UploadMasiveInventory",
    props: {
        title: {
            type: String,
            default: "Carga Masiva de Inventario",
        },
        products: {
            type: Array,
            default: () => [],
        },
        providers: {
            type: Array,
            default: () => [],
        },
        zones: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        currentFile: null,
        excelData: [],
        headers: headersMassiveInventory,
        loading: false,
        error: false,
        search: "",
        hasErrors: false,
    }),
    mounted() {
        console.log("Products:", this.products);
        console.log("Providers:", this.providers);
        console.log("Zones:", this.zones);
    },
    methods: {
        // Limpia la tabla si el usuario cambia el archivo
        resetData() {
            this.excelData = [];
            this.hasErrors = false;
            this.error = false;
        },

        cancelAction() {
            this.$emit("cancelStock");
            this.currentFile = null;
            this.resetData();
        },

        confirmData() {
            const validItems = this.excelData.filter((row) => row.flag);

            // Agrupar por zoneId + supplierId
            const groupedMap = new Map();

            validItems.forEach((row) => {
                const key = `${row.zoneId}-${row.supplierId}`;
                if (!groupedMap.has(key)) {
                    groupedMap.set(key, {
                        zoneId: row.zoneId,
                        supplierId: row.supplierId,
                        reference: row.reference,
                        items: [],
                    });
                }
                const group = groupedMap.get(key);
                group.items.push({
                    id: row.productId,
                    quantity: row.quantity,
                });
            });

            // Convertir map a array de objetos
            const payload = Array.from(groupedMap.values()).map((group) => ({
                items: group.items,
                reference: group.reference,
                origin: group.supplierId,
                zone: group.zoneId,
            }));

            this.$emit("executeUploadMassiveInventory", payload);
        },

        readExcelFile() {
            if (!this.currentFile) return;

            this.loading = true;
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = e.target.result;

                    // 1. Leer el libro
                    const workbook = XLSX.read(data, { type: "array" });

                    // 2. Tomar la primera hoja
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    // 3. Convertir a JSON
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    console.log("🚀 Datos leídos del Excel:", jsonData);

                    if (jsonData.length > 0) {
                        const processedData = jsonData.map((excelRow) => {
                            return this.processRow(excelRow);
                        });

                        this.excelData = processedData;
                    } else {
                        console.warn(
                            "El archivo Excel está vacío o no se pudo leer correctamente.",
                        );
                    }
                } catch (error) {
                    console.error("Error al leer el archivo Excel:", error);
                } finally {
                    this.loading = false;
                }
            };

            reader.readAsArrayBuffer(this.currentFile);
        },

        processRow(excelRow) {
            const errors = [];

            // Validar producto (codProd debe existir en products.name)
            const codProd = excelRow.codProd;
            const product = this.products.find(
                (p) => String(p.name) === String(codProd),
            );
            const productId = product?.id ?? null;
            const productName = product?.description ?? codProd;
            if (!productId) {
                errors.push(`Producto '${codProd}' no existe`);
            }

            // Validar cantidad
            const quantity = excelRow.cantidad;
            if (
                quantity === null ||
                quantity === undefined ||
                quantity === ""
            ) {
                errors.push("Cantidad es obligatoria");
            }

            // Validar referencia
            const reference = excelRow.reference
                ? String(excelRow.reference).trim()
                : "";
            if (!reference) {
                errors.push("Referencia es obligatoria");
            }

            // Validar proveedor (debe existir en suppliers)
            const providerText = excelRow.provider
                ? String(excelRow.provider).trim().toUpperCase()
                : "";
            const supplier = this.providers.find(
                (s) => s.text.toUpperCase() === providerText,
            );
            const supplierId = supplier?.value ?? null;
            const supplierName = supplier?.text ?? excelRow.provider;
            if (!supplierId) {
                errors.push(`Proveedor '${excelRow.provider}' no existe`);
            }

            // Validar zona (debe existir en zones)
            const zoneText = excelRow.zone
                ? String(excelRow.zone).trim().toUpperCase()
                : "";
            const zone = this.zones.find(
                (z) => z.text.toUpperCase() === zoneText,
            );
            const zoneId = zone?.value ?? null;
            const zoneName = zone?.text ?? excelRow.zone;
            if (!zoneId) {
                errors.push(`Zona '${excelRow.zone}' no existe`);
            }

            const flag = errors.length === 0;
            if (!flag) this.hasErrors = true;

            return {
                flag,
                errors: errors.join(", "),
                productId,
                product: productName,
                quantity,
                reference,
                supplierId,
                supplier: supplierName,
                zoneId,
                zone: zoneName,
            };
        },
    },
};
</script>

<style scoped>
.custom-font {
    font-size: 0.9rem;
}
</style>
